import React, { createContext, useContext, useState, useEffect } from 'react';
import { CognitoService } from '../services/cognitoService';
import { SSOService } from '../services/ssoService';

// Create Auth Context
const AuthContext = createContext();

// Hook to use auth context
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

// Auth Provider Component
export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Check if user is already authenticated on component mount
    useEffect(() => {
        checkAuthState();
    }, []);

    const checkAuthState = async () => {
        try {
            setLoading(true);
            
            // Check for OAuth callback first
            if (SSOService.isCallback()) {
                try {
                    await handleOAuthCallback();
                    return;
                } catch (ssoErr) {
                    console.error('OAuth callback error:', ssoErr);
                    setError(ssoErr.message || 'SSO authentication failed');
                    // Clean up URL even on error
                    window.history.replaceState({}, document.title, '/');
                }
            }
            
            // Check for regular Cognito session
            const currentUser = await CognitoService.getCurrentUser();
            setUser(currentUser);
        } catch (err) {
            // User not authenticated or session expired
            setUser(null);
        } finally {
            setLoading(false);
        }
    };

    const login = async (email, password) => {
        try {
            setLoading(true);
            setError(null);
            const result = await CognitoService.signIn(email, password);
            
            if (result.newPasswordRequired) {
                // Return the result for new password challenge
                return result;
            }
            
            setUser(result);
            return result;
        } catch (err) {
            setError(err.message || 'Login failed');
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const signup = async (email, password, attributes = {}) => {
        try {
            setLoading(true);
            setError(null);
            const result = await CognitoService.signUp(email, password, attributes);
            return result;
        } catch (err) {
            setError(err.message || 'Signup failed');
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const confirmSignup = async (email, confirmationCode) => {
        try {
            setLoading(true);
            setError(null);
            const result = await CognitoService.confirmSignUp(email, confirmationCode);
            return result;
        } catch (err) {
            setError(err.message || 'Confirmation failed');
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const setNewPassword = async (cognitoUser, newPassword, attributes = {}) => {
        try {
            setLoading(true);
            setError(null);
            const result = await CognitoService.setNewPassword(cognitoUser, newPassword, attributes);
            setUser(result);
            return result;
        } catch (err) {
            setError(err.message || 'Password update failed');
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const logout = async () => {
        try {
            CognitoService.signOut();
            setUser(null);
            setError(null);
        } catch (err) {
            setError(err.message || 'Logout failed');
        }
    };

    const forgotPassword = async (email) => {
        try {
            setLoading(true);
            setError(null);
            const result = await CognitoService.forgotPassword(email);
            return result;
        } catch (err) {
            setError(err.message || 'Forgot password failed');
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const confirmForgotPassword = async (email, confirmationCode, newPassword) => {
        try {
            setLoading(true);
            setError(null);
            const result = await CognitoService.confirmForgotPassword(email, confirmationCode, newPassword);
            return result;
        } catch (err) {
            setError(err.message || 'Password reset failed');
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const resendConfirmationCode = async (email) => {
        try {
            setLoading(true);
            setError(null);
            const result = await CognitoService.resendConfirmationCode(email);
            return result;
        } catch (err) {
            setError(err.message || 'Resend confirmation failed');
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const handleOAuthCallback = async () => {
        try {
            const user = await SSOService.handleCallback();
            setUser(user);
            SSOService.cleanupCallbackUrl();
        } catch (error) {
            console.error('OAuth callback error:', error);
            throw error;
        }
    };



    const loginWithSSO = () => {
        try {
            setError(null);
            SSOService.redirectToSSO();
        } catch (err) {
            console.error('SSO login error:', err);
            setError(err.message || 'SSO login failed');
        }
    };

    const clearError = () => {
        setError(null);
    };

    const isAuthenticated = () => {
        return user !== null;
    };


   
    const getAccessToken = () => {
        return user?.tokens?.idToken || null;
    };

    const getUserAttributes = () => {
        return user?.attributes || {};
    };

    const value = {
        user,
        loading,
        error,
        login,
        loginWithSSO,
        signup,
        confirmSignup,
        setNewPassword,
        logout,
        forgotPassword,
        confirmForgotPassword,
        resendConfirmationCode,
        clearError,
        isAuthenticated,
        getAccessToken,
        getUserAttributes,
        checkAuthState,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContext; 